/**
 * Wraps HTMLAudioElement for voice message playback.
 *
 * - Manages object URL lifecycle (create on play, revoke on cleanup).
 * - iOS Safari: removes src + calls load() to clear the Control Center entry.
 * - Exposes simple play/pause/resume/seek/stop API with typed callbacks.
 */

import type { PlayerEvents } from "./types";

export class AudioPlayer {
  /**
   * Checks whether the current browser can play a given audio MIME type.
   * Used to detect unsupported formats on older browsers (e.g. WebM on Safari < 15)
   * before attempting playback, so the UI can show a friendly message.
   *
   * @param mimeType - The MIME type to check (e.g. `"audio/webm;codecs=opus"`).
   * @returns `true` if the browser reports "probably" or "maybe" support.
   */
  static canPlay(mimeType: string): boolean {
    if (!mimeType) {
      return true; // Unknown type — let the browser try
    }
    const audio = document.createElement("audio");
    const result = audio.canPlayType(mimeType);
    return result === "probably" || result === "maybe";
  }

  /** The underlying HTMLAudioElement, null when no audio is loaded */
  private audio: HTMLAudioElement | null = null;

  /** Object URL created from the audio Blob (must be revoked on cleanup) */
  private objectUrl: string | null = null;

  /** Registered event callbacks for playback lifecycle events */
  private listeners: Partial<PlayerEvents> = {};

  /**
   * Creates a new AudioPlayer instance.
   *
   * @param events - Optional initial event callbacks for playback events.
   */
  constructor(events?: Partial<PlayerEvents>) {
    if (events) {
      this.listeners = events;
    }
  }

  /**
   * Register or update an event listener after construction.
   *
   * @param event - The event name (e.g. `"onTimeUpdate"`, `"onEnded"`).
   * @param handler - The callback function for this event.
   */
  on<K extends keyof PlayerEvents>(event: K, handler: PlayerEvents[K]): void {
    this.listeners[event] = handler;
  }

  /** Remove a specific event listener */
  off<K extends keyof PlayerEvents>(event: K): void {
    delete this.listeners[event];
  }

  /**
   * Creates an audio element, sets the blob as source, and starts playback.
   * If already playing, cleans up the current element and creates a new one.
   *
   * @param blob - The audio Blob to play (typically fetched from the server).
   * @throws {Error} If the browser fails to decode or play the audio.
   */
  async play(blob: Blob): Promise<void> {
    this.cleanup();

    const audio = new Audio();
    this.audio = audio;

    this.objectUrl = URL.createObjectURL(blob);
    audio.src = this.objectUrl;
    audio.preload = "auto";

    audio.ontimeupdate = () => {
      this.listeners.onTimeUpdate?.(audio.currentTime);
    };

    audio.onloadedmetadata = () => {
      this.listeners.onDurationChange?.(audio.duration);
    };

    audio.onended = () => {
      this.listeners.onEnded?.();
      this.cleanup();
    };

    audio.onerror = () => {
      const msg = audio.error?.message ?? "Audio playback error";
      this.listeners.onError?.(new Error(msg));
      this.cleanup();
    };

    audio.load();
    await audio.play();
  }

  /** Pause playback. Can be resumed with `resume()`. */
  pause(): void {
    this.audio?.pause();
  }

  /** Resume after pause. */
  async resume(): Promise<void> {
    if (this.audio?.paused) {
      await this.audio.play();
    }
  }

  /**
   * Seek to a specific time in the audio.
   *
   * @param time - The target time in seconds.
   */
  seek(time: number): void {
    if (this.audio) {
      this.audio.currentTime = time;
    }
  }

  /** Stop playback and release resources. */
  stop(): void {
    this.cleanup();
  }

  /** Whether audio is actively playing (not paused, not ended). */
  get isPlaying(): boolean {
    return this.audio !== null && !this.audio.paused && !this.audio.ended;
  }

  /** Whether audio is paused (has data, just not playing). */
  get isPaused(): boolean {
    return (
      this.audio !== null && this.audio.paused && this.audio.currentTime > 0
    );
  }

  /** Current playback time in seconds. */
  get currentTime(): number {
    return this.audio?.currentTime ?? 0;
  }

  /** Total duration in seconds. */
  get duration(): number {
    return this.audio?.duration ?? 0;
  }

  /**
   * Fully cleans up the audio element and object URL.
   * On iOS Safari: removeAttribute('src') + load() clears the
   * media session from Control Center.
   */
  private cleanup(): void {
    if (this.audio) {
      this.audio.pause();

      // iOS Safari keeps the media session active in Control Center unless
      // the element is fully unloaded
      this.audio.removeAttribute("src");
      this.audio.load();

      this.audio.ontimeupdate = null;
      this.audio.onloadedmetadata = null;
      this.audio.onended = null;
      this.audio.onerror = null;
      this.audio = null;
    }

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  /** Tear down everything. Call on component unmount. */
  destroy(): void {
    this.cleanup();
    this.listeners = {};
  }
}
